import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import useSeo from "../hooks/useSeo";
import { assetUrl } from "../utils/api";
import RecommendPeopleRail from "../components/RecommendPeopleRail";
import {
  fetchLoveMapCoupleInvites,
  fetchLoveMapCoupleList,
  fetchLoveMapCoupleWith,
  generateLoveMapCouple,
  inviteLoveMapCouple,
  requestLoveMapCoupleAiReport,
  respondLoveMapCoupleInvite,
} from "../api/loveMapApi";

function DualBars({ diffs = [], nameA = "A", nameB = "B" }) {
  if (!diffs.length) return null;
  return (
    <div className="space-y-4">
      <div className="flex justify-between text-xs text-gray-500 px-1">
        <span className="text-rose-500 font-medium">● {nameA}</span>
        <span>同维对照</span>
        <span className="text-violet-500 font-medium">● {nameB}</span>
      </div>
      {diffs.map((d) => {
        const left = Math.min(d.a, d.b);
        const right = Math.max(d.a, d.b);
        const aLeft = d.a <= d.b;
        return (
          <div key={d.key} className="space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700">{d.label}</span>
              <span className="text-xs text-gray-400">差距 {d.gap}</span>
            </div>
            <div className="relative h-3 bg-white rounded-full border border-rose-100">
              <div
                className="absolute top-0 h-full rounded-full bg-gradient-to-r from-rose-200/80 to-violet-200/80"
                style={{ left: `${left}%`, width: `${Math.max(2, right - left)}%` }}
              />
              <span
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-rose-500 border-2 border-white shadow"
                style={{ left: `calc(${d.a}% - 6px)` }}
              />
              <span
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-violet-500 border-2 border-white shadow"
                style={{ left: `calc(${d.b}% - 6px)` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-gray-500">
              <span className={aLeft ? "text-rose-600" : "text-violet-600"}>
                {aLeft ? `${nameA} ${d.a}` : `${nameB} ${d.b}`}
              </span>
              <span className={!aLeft ? "text-rose-600" : "text-violet-600"}>
                {!aLeft ? `${nameA} ${d.a}` : `${nameB} ${d.b}`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PersonCard({ user, accent = "rose" }) {
  if (!user) return null;
  const border = accent === "violet" ? "border-violet-100 bg-violet-50/70" : "border-rose-100 bg-rose-50/70";
  const label = accent === "violet" ? "text-violet-600" : "text-rose-600";
  return (
    <div className={`rounded-2xl border p-4 flex gap-3 items-center ${border}`}>
      <img
        src={user.avatar ? assetUrl(user.avatar) : "https://via.placeholder.com/56"}
        alt=""
        className="w-12 h-12 rounded-full object-cover"
      />
      <div className="min-w-0">
        <p className="font-semibold truncate">{user.name || "会员"}</p>
        <p className={`text-xs ${label}`}>{user.personality_label || "爱情地图画像"}</p>
        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{user.one_liner}</p>
      </div>
    </div>
  );
}

export default function LoveMapCouplePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const partnerParam = searchParams.get("partner");
  const userId = localStorage.getItem("user_id");

  useSeo({
    title: "双人爱情地图_情侣关系探索 - AI婚恋平台",
    metaDescription:
      "双方授权后生成双人爱情地图与情侣关系提示。差异不是问题，沟通才是关键。",
    path: "/love-map/couple",
  });

  const [invites, setInvites] = useState({ incoming: [], outgoing: [], accepted: [] });
  const [maps, setMaps] = useState([]);
  const [active, setActive] = useState(null);
  const [partnerId, setPartnerId] = useState(partnerParam || "");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const reload = async () => {
    if (!userId) return;
    const [inv, list] = await Promise.all([
      fetchLoveMapCoupleInvites(),
      fetchLoveMapCoupleList(),
    ]);
    setInvites(inv);
    setMaps(list.items || []);
  };

  useEffect(() => {
    if (!userId) return;
    setBusy(true);
    reload()
      .catch((err) => setError(err.message || "加载失败"))
      .finally(() => setBusy(false));
  }, [userId]);

  useEffect(() => {
    if (!userId || !partnerParam) return;
    setPartnerId(partnerParam);
    setBusy(true);
    fetchLoveMapCoupleWith(Number(partnerParam))
      .then(setActive)
      .catch(() => {
        /* 可能尚未生成，保留邀请流程 */
      })
      .finally(() => setBusy(false));
  }, [userId, partnerParam]);

  const sendInvite = async () => {
    const tid = Number(partnerId);
    if (!tid) {
      setError("请填写对方用户 ID");
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await inviteLoveMapCouple(tid, "想一起看看我们的双人爱情地图");
      setMsg("邀请已发送，等待对方授权");
      await reload();
    } catch (err) {
      setError(err.message || "邀请失败");
    } finally {
      setBusy(false);
    }
  };

  const respond = async (inviteId, action) => {
    setBusy(true);
    setError("");
    try {
      const data = await respondLoveMapCoupleInvite(inviteId, action);
      if (data.couple_map) {
        setActive(data.couple_map);
        setSearchParams({ partner: String(data.couple_map.user_a.id === Number(userId) ? data.couple_map.user_b.id : data.couple_map.user_a.id) });
      }
      await reload();
      setMsg(action === "accept" ? "已授权并生成双人地图" : "已拒绝邀请");
    } catch (err) {
      setError(err.message || "操作失败");
    } finally {
      setBusy(false);
    }
  };

  const refreshMap = async (pid) => {
    setBusy(true);
    setError("");
    try {
      const data = await generateLoveMapCouple(pid);
      setActive(data.couple_map);
      await reload();
    } catch (err) {
      setError(err.message || "生成失败");
    } finally {
      setBusy(false);
    }
  };

  const runAi = async () => {
    if (!active?.id) return;
    setAiBusy(true);
    setError("");
    try {
      const data = await requestLoveMapCoupleAiReport(active.id);
      setActive(data.couple_map);
    } catch (err) {
      setError(err.message || "生成报告失败");
    } finally {
      setAiBusy(false);
    }
  };

  if (!userId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-violet-50 to-sky-50 flex flex-col">
        <Header />
        <main className="flex-1 px-4 py-16 text-center space-y-4">
          <h1 className="text-2xl font-bold">双人爱情地图</h1>
          <p className="text-gray-500 text-sm">需要登录，且双方授权后才可生成</p>
          <Link to="/login" className="inline-block px-6 py-3 rounded-full bg-rose-500 text-white font-semibold">
            去登录
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const report = active?.relationship_report || {};
  const nameA = active?.user_a?.name || "A";
  const nameB = active?.user_b?.name || "B";

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-violet-50 to-sky-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8 md:py-10">
        <div className="max-w-6xl mx-auto flex gap-6 items-start justify-center">
          <div className="w-full max-w-lg space-y-5">
          <div className="lg:hidden">
            <RecommendPeopleRail layout="strip" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-violet-500 font-medium">Phase 6–7</p>
              <h1 className="text-2xl font-extrabold text-gray-800">双人爱情地图</h1>
              <p className="text-xs text-gray-500 mt-1">双方授权 · 对照画像 · 情侣关系提示</p>
            </div>
            <Link to="/love-map" className="text-sm text-rose-600">
              返回
            </Link>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
          {msg && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
              {msg}
            </div>
          )}

          <section className="bg-white/95 rounded-3xl border border-rose-100 p-5 space-y-3">
            <h2 className="font-bold text-gray-800">邀请对方一起看</h2>
            <p className="text-xs text-gray-500">
              输入对方用户 ID（可在对方资料页地址栏看到）。双方都需完成个人爱情地图。
            </p>
            <div className="flex gap-2">
              <input
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                placeholder="对方用户 ID"
                className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
              />
              <button
                type="button"
                disabled={busy}
                onClick={sendInvite}
                className="px-4 py-2.5 rounded-xl bg-violet-500 text-white text-sm font-semibold disabled:opacity-60"
              >
                发送邀请
              </button>
            </div>
          </section>

          {(invites.incoming || []).length > 0 && (
            <section className="bg-white rounded-3xl border border-violet-100 p-5 space-y-3">
              <h2 className="font-bold text-gray-800">待我授权</h2>
              {invites.incoming.map((inv) => (
                <div key={inv.id} className="flex items-center gap-3 border border-violet-50 rounded-2xl p-3">
                  <img
                    src={
                      inv.other_user?.avatar
                        ? assetUrl(inv.other_user.avatar)
                        : "https://via.placeholder.com/48"
                    }
                    alt=""
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{inv.other_user?.name || "会员"}</p>
                    <p className="text-xs text-gray-500">{inv.message || "邀请生成双人爱情地图"}</p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => respond(inv.id, "accept")}
                    className="text-xs px-3 py-1.5 rounded-lg bg-violet-500 text-white"
                  >
                    接受
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => respond(inv.id, "decline")}
                    className="text-xs px-3 py-1.5 rounded-lg border"
                  >
                    拒绝
                  </button>
                </div>
              ))}
            </section>
          )}

          {(invites.outgoing || []).length > 0 && (
            <section className="bg-white/90 rounded-3xl border border-amber-100 p-5 space-y-2">
              <h2 className="font-bold text-gray-800 text-sm">已发出 · 等待对方</h2>
              {invites.outgoing.map((inv) => (
                <p key={inv.id} className="text-sm text-gray-600">
                  → {inv.other_user?.name || inv.invitee_id}（待授权）
                </p>
              ))}
            </section>
          )}

          {maps.length > 0 && (
            <section className="bg-white rounded-3xl border border-rose-100 p-5 space-y-3">
              <h2 className="font-bold text-gray-800">我的双人地图</h2>
              {maps.map((m) => {
                const other =
                  m.user_a?.id === Number(userId) ? m.user_b : m.user_a;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => {
                      setActive(m);
                      setSearchParams({ partner: String(other.id) });
                    }}
                    className="w-full text-left flex items-center gap-3 p-3 rounded-2xl border border-rose-50 hover:bg-rose-50/50"
                  >
                    <img
                      src={
                        other?.avatar
                          ? assetUrl(other.avatar)
                          : "https://via.placeholder.com/48"
                      }
                      alt=""
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{other?.name || "TA"}</p>
                      <p className="text-xs text-violet-600">契合参考 {m.alignment_score}%</p>
                    </div>
                    <button
                      type="button"
                      className="text-xs text-gray-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshMap(other.id);
                      }}
                    >
                      刷新
                    </button>
                  </button>
                );
              })}
            </section>
          )}

          {active && (
            <section className="space-y-4 animate-[fadeIn_0.35s_ease]">
              <header className="text-center space-y-2">
                <h2 className="text-xl font-extrabold text-gray-800">我们的双人地图</h2>
                <div className="inline-flex items-baseline gap-1 bg-white border border-rose-100 rounded-full px-4 py-1.5">
                  <span className="text-xs text-gray-500">契合参考</span>
                  <span className="text-2xl font-bold text-rose-500">{active.alignment_score}</span>
                </div>
                <p className="text-xs text-gray-400">差异不是问题，适合才是关键</p>
              </header>

              <div className="grid gap-3">
                <PersonCard user={active.user_a} accent="rose" />
                <PersonCard user={active.user_b} accent="violet" />
              </div>

              <div className="bg-white rounded-3xl border border-rose-100 p-5">
                <h3 className="font-bold text-gray-800 mb-3">八维连线</h3>
                <DualBars diffs={active.diffs || []} nameA={nameA} nameB={nameB} />
              </div>

              {(active.fit_notes || active.complement_notes) && (
                <div className="bg-white rounded-3xl border border-rose-100 p-5 space-y-3 text-sm text-gray-600">
                  {active.fit_notes && (
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">契合之处</p>
                      <p>{active.fit_notes}</p>
                    </div>
                  )}
                  {active.complement_notes && (
                    <div>
                      <p className="font-semibold text-gray-800 mb-1">差异与互补</p>
                      <p>{active.complement_notes}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-gradient-to-br from-white to-violet-50/50 rounded-3xl border border-violet-100 p-5 space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-gray-800">情侣关系 AI</h3>
                  <span className="text-[11px] text-gray-400">
                    {active.source === "llm" || report.source === "llm" ? "AI 润色" : "规则解读"}
                  </span>
                </div>
                {report.summary && (
                  <p className="text-sm text-gray-600 leading-relaxed">{report.summary}</p>
                )}
                {(report.strengths || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-rose-600 mb-1">关系优势</p>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {report.strengths.map((t) => (
                        <li key={t}>· {t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(report.challenges || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-1">可能的张力</p>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {report.challenges.map((t) => (
                        <li key={t}>· {t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(report.communication_tips || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-violet-600 mb-1">沟通提示</p>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {report.communication_tips.map((t) => (
                        <li key={t}>· {t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(report.weekly_practice || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-1">本周小练习</p>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {report.weekly_practice.map((t) => (
                        <li key={t}>· {t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  type="button"
                  disabled={aiBusy}
                  onClick={runAi}
                  className="w-full py-3 rounded-full bg-violet-500 text-white font-semibold disabled:opacity-60"
                >
                  {aiBusy ? "正在生成…" : "刷新情侣关系 AI 报告"}
                </button>
                <p className="text-[11px] text-gray-400">
                  {report.disclaimer || active.disclaimer}
                </p>
              </div>
            </section>
          )}

          {!active && !busy && maps.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-6">
              还没有双人地图。邀请对方授权后即可生成。
            </p>
          )}
          </div>

          <div className="hidden lg:block shrink-0">
            <RecommendPeopleRail layout="sidebar" />
          </div>
        </div>
      </main>
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
